"use client"

import * as React from "react"
import Image from "next/image"
import Link from "next/link"
import {
  ArrowRightIcon,
  CheckIcon,
  ChevronDownIcon,
  FlameIcon,
  PlayIcon,
  XIcon,
} from "lucide-react"

import { cn } from "@/lib/utils"
import {
  bilanSemaine,
  type BilanSemaine,
  JOURS,
  JOURS_COURTS,
  type Client,
  type CommentFaire,
  type Statut,
  type Tache,
} from "@/lib/data/types"
import { Wordmark } from "@/components/site/wordmark"
import { enregistrerReponse } from "@/app/mon-espace/actions"
import bandeau from "@/assets/images/hero-terrasse.webp"

/** Le statut dit à voix haute : sert l'aria-label des pastilles et l'annonce
    live quand on note une consigne. */
const LIBELLE_STATUT: Record<Statut, string> = {
  fait: "fait",
  manque: "pas fait",
  attente: "sans réponse",
}

/** Une consigne rattachée à un jour précis, telle qu'elle apparaît en haut. */
type Ligne = { tache: Tache; jour: number; enRetard: boolean }

/**
 * Le mot qui dit au client où il en est de sa semaine.
 *
 * Pas une note, pas un score : une phrase honnête. La pleine reconnaissance
 * (« Semaine tenue ») n'arrive que quand tout est fait — sinon elle ne
 * vaudrait rien. Tant qu'il reste des créneaux échus sans réponse, on le nomme
 * (« à rattraper »). Le reste — aujourd'hui, l'à-venir — n'est pas un retard.
 */
function lireTenue(bilan: BilanSemaine): {
  titre: string
  detail?: string
  fort?: boolean
} {
  if (bilan.total > 0 && bilan.fait === bilan.total)
    return { titre: "Semaine tenue", fort: true }
  if (bilan.aRattraper > 0) {
    return { titre: "À rattraper", detail: `${bilan.aRattraper} en attente` }
  }
  if (bilan.fait === 0 && bilan.manque === 0) return { titre: "La semaine commence" }
  return { titre: "Vous tenez le cap" }
}

/**
 * L'espace client, repensé pour la clarté ET l'élan.
 *
 * Trois zones, jamais sept. Et surtout : une hiérarchie qui *tire*. Quand une
 * séance guidée tombe aujourd'hui, elle prend toute la lumière — une carte
 * héros, un seul geste (« Commencer »). Quand la journée est faite, l'app le
 * *fête* sobrement. Et le ressort qui fait revenir — la série de semaines
 * tenues — se lit dès l'accueil, plus enfoui dans le parcours.
 *
 *  1. AUJOURD'HUI — l'action, impossible à rater. Séance du jour en héros,
 *     consignes simples en dessous, récompense quand tout est fait.
 *  2. MA SEMAINE — l'élan : la série, le « X sur Y », la semaine en lecture.
 *  3. AVEC LE COACH — le lien : prochain rendez-vous et mot de la semaine.
 */
function Dashboard({
  client,
  aujourdhui,
  dateLisible,
  serie,
  datesSemaine,
  semainesAVenir,
}: {
  client: Client
  /** Lundi = 0. Calculé côté serveur pour éviter tout écart d'hydratation. */
  aujourdhui: number
  dateLisible: string
  /** Semaines actives consécutives — le ressort d'engagement, montré haut. */
  serie: number
  /** Quantième de chaque jour de la semaine courante, lundi en tête. Sept entrées. */
  datesSemaine: number[]
  /** L'horizon de l'agenda : les prochaines semaines, datées mais pas encore remplies. */
  semainesAVenir: { libelle: string; court: string }[]
}) {
  const [taches, setTaches] = React.useState<Tache[]>(client.semaine.taches)

  /* Ce qu'un lecteur d'écran entend après qu'on a noté une consigne. */
  const [annonce, setAnnonce] = React.useState("")

  const [, demarrerEcriture] = React.useTransition()

  /* Écrit un statut : d'abord à l'écran (optimiste, immédiat), puis on le
     persiste en tâche de fond. Si l'enregistrement échoue, on revient à l'état
     précédent et on le dit — l'app ne fait jamais semblant d'avoir gardé. */
  function setStatut(tacheId: string, jour: number, statut: Statut) {
    const tache = taches.find((t) => t.id === tacheId)
    const precedent = tache?.statuts[jour] ?? "attente"

    appliquerStatut(tacheId, jour, statut)
    if (tache) {
      setAnnonce(`${tache.label}, ${JOURS[jour]} : ${LIBELLE_STATUT[statut]}`)
    }

    demarrerEcriture(async () => {
      try {
        await enregistrerReponse(client.id, tacheId, jour, statut)
      } catch {
        appliquerStatut(tacheId, jour, precedent)
        setAnnonce("Réponse non enregistrée, réessayez.")
      }
    })
  }

  function appliquerStatut(tacheId: string, jour: number, statut: Statut) {
    setTaches((current) =>
      current.map((tache) =>
        tache.id === tacheId
          ? { ...tache, statuts: { ...tache.statuts, [jour]: statut } }
          : tache
      )
    )
  }

  /* Ce qui appelle un geste aujourd'hui : le jour même, plus les jours passés
     restés sans réponse. Les rattrapages remontent ici plutôt que de se cacher
     dans un tableau — sinon personne ne les trouve. */
  const aRepondre: Ligne[] = []
  for (const tache of taches) {
    for (const jour of tache.jours) {
      const statut = tache.statuts[jour] ?? "attente"
      if (jour === aujourdhui) aRepondre.push({ tache, jour, enRetard: false })
      else if (jour < aujourdhui && statut === "attente") {
        aRepondre.push({ tache, jour, enRetard: true })
      }
    }
  }
  aRepondre.sort((a, b) => a.jour - b.jour)

  /* Deux temps, pas deux blocs bavards : ce qui attend une réponse reste en
     avant ; ce qui est répondu (fait ou pas fait) recule dans une liste sobre,
     qu'on défait d'un tap. La séance guidée passe devant les consignes. */
  const aFaire = aRepondre.filter(
    (l) => (l.tache.statuts[l.jour] ?? "attente") === "attente"
  )
  const repondu = aRepondre.filter(
    (l) => (l.tache.statuts[l.jour] ?? "attente") !== "attente"
  )
  const seancesAFaire = aFaire.filter((l) => l.tache.deroule)
  const consignesAFaire = aFaire.filter((l) => !l.tache.deroule)

  const bilan = bilanSemaine(taches, aujourdhui)
  const tenue = lireTenue(bilan)
  const semaineVide = taches.length === 0

  /* Le jour où tombe la prochaine séance, lu dans son libellé (« Jeudi 13 août »)
     pour l'épingler dans l'agenda. Absent si le jour n'y est pas nommé. */
  const rdvJour = jourDuRdv(client.prochaineSeance.quand)

  return (
    <div className="pb-28">
      {/* Annonce discrète des changements de statut pour les lecteurs d'écran. */}
      <p role="status" aria-live="polite" className="sr-only">
        {annonce}
      </p>

      {/* ------------------------------------------------ bandeau */}
      <div className="relative h-[240px] w-full overflow-hidden sm:h-[280px]">
        <Image
          src={bandeau}
          alt=""
          fill
          priority
          placeholder="blur"
          sizes="100vw"
          className="object-cover object-[center_58%]"
        />
        <div
          aria-hidden
          className="absolute inset-0 bg-[linear-gradient(to_top,rgba(0,0,0,0.96)_0%,rgba(0,0,0,0.72)_38%,rgba(0,0,0,0.25)_72%,rgba(0,0,0,0.45)_100%)]"
        />

        <div className="absolute inset-x-0 top-0 mx-auto flex max-w-[560px] items-center justify-between px-gutter pt-8">
          <Wordmark className="text-[1.125rem]" />
          <span className="font-heading text-xs tracking-brand text-white/70 uppercase">
            {client.semaine.libelle}
          </span>
        </div>

        <div className="absolute inset-x-0 bottom-0 mx-auto max-w-[560px] px-gutter pb-7">
          <h1 className="font-heading text-[clamp(2.25rem,8vw,3rem)] leading-none font-medium uppercase">
            Bonjour {client.prenom}
          </h1>
          <p className="mt-2 text-[0.9375rem] text-white/70 first-letter:uppercase">
            {dateLisible}
          </p>
        </div>
      </div>

      <div className="mx-auto w-full max-w-[560px] px-gutter pt-10">
        {/* ============================================== 1 · AUJOURD'HUI */}
        <section>
          <TitreZone
            titre="Aujourd'hui"
            indication={aFaire.length > 0 ? String(aFaire.length) : undefined}
          />

          {aFaire.length > 0 ? (
            <div className="grid gap-3">
              {seancesAFaire.map((ligne) => (
                <CarteSeance
                  key={`${ligne.tache.id}-${ligne.jour}`}
                  ligne={ligne}
                  onStatut={setStatut}
                />
              ))}
              {consignesAFaire.map((ligne) => (
                <CarteJour
                  key={`${ligne.tache.id}-${ligne.jour}`}
                  ligne={ligne}
                  onStatut={setStatut}
                />
              ))}
            </div>
          ) : repondu.length === 0 ? (
            <EtatVide titre={semaineVide ? "Pas encore de consigne" : "Rien de prévu"} />
          ) : null}

          {/* Le répondu : ça recule. Petites lignes cochées, un tap pour défaire. */}
          {repondu.length > 0 ? (
            <ul
              className={cn(
                "grid gap-0.5",
                aFaire.length > 0 && "mt-4 border-t border-border pt-3"
              )}
            >
              {repondu.map((ligne) => (
                <LigneFaite
                  key={`${ligne.tache.id}-${ligne.jour}`}
                  ligne={ligne}
                  onStatut={setStatut}
                />
              ))}
            </ul>
          ) : null}
        </section>

        {/* ============================================== 2 · MON AGENDA
            La semaine en cours *et* celles d'après, comme un agenda qu'on
            feuillette. La carte du jour reste en tête (zone 1) ; ici on prend
            de la hauteur : ce qui est prévu chaque jour, le RDV du coach épinglé,
            et l'horizon des semaines suivantes — pour se projeter, pas seulement
            cocher aujourd'hui. */}
        {semaineVide ? null : (
          <section className="mt-12">
            <TitreZone titre="Mon agenda" discret />

            <div className="overflow-x-auto snap-x snap-mandatory [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
              <div className="flex items-stretch gap-3 pr-1">
                <div className="w-[86%] shrink-0 snap-start">
                  <AgendaSemaine
                    taches={taches}
                    aujourdhui={aujourdhui}
                    datesSemaine={datesSemaine}
                    rdvJour={rdvJour}
                    rdvLabel={`RDV ${client.coach}`}
                    tenue={tenue}
                    bilan={bilan}
                    serie={serie}
                  />
                </div>

                {semainesAVenir.map((s) => (
                  <div key={s.court} className="w-[86%] shrink-0 snap-start">
                    <AgendaAVenir libelle={s.libelle} coach={client.coach} />
                  </div>
                ))}
              </div>
            </div>

            {semainesAVenir.length > 0 ? (
              <p className="mt-3 font-heading text-[0.6875rem] tracking-brand text-faint uppercase">
                Glissez pour les semaines suivantes →
              </p>
            ) : null}
          </section>
        )}

        {/* ============================================== 3 · AVEC LE COACH */}
        <section className="mt-12">
          <TitreZone titre={`Avec ${client.coach}`} discret />

          <div className="grid gap-3">
            {client.prochaineSeance.quand.trim() ? (
              <Link
                href="/mon-espace/rendez-vous"
                className="group flex items-center gap-4 rounded-lg border border-border bg-card px-5 py-5 outline-none transition-colors hover:border-border-strong focus-visible:ring-3 focus-visible:ring-ring/50"
              >
                <div className="min-w-0 flex-1">
                  <p className="font-heading text-xs tracking-brand text-faint uppercase">
                    Prochaine séance
                  </p>
                  <p className="mt-2 font-heading text-[1.375rem] leading-tight uppercase">
                    {client.prochaineSeance.quand}
                  </p>
                  <p className="mt-2 text-[0.9375rem] text-faint">
                    {client.prochaineSeance.lieu.trim()
                      ? `${client.prochaineSeance.lieu} · avec ${client.coach}`
                      : `avec ${client.coach}`}
                    <span className="text-muted-foreground"> · Voir le brief</span>
                  </p>
                </div>
                <ArrowRightIcon
                  aria-hidden
                  className="size-5 shrink-0 text-faint transition-transform group-hover:translate-x-0.5 group-hover:text-foreground"
                />
              </Link>
            ) : (
              <EtatVide titre="Prochaine séance à caler">
                Elle s&apos;affichera ici dès que {client.coach} l&apos;aura
                planifiée.
              </EtatVide>
            )}

            {client.semaine.mot.trim() ? (
              <Link
                href="/mon-espace/fil"
                className="group block rounded-lg border border-border bg-card px-5 py-5 outline-none transition-colors hover:border-border-strong focus-visible:ring-3 focus-visible:ring-ring/50"
              >
                <p className="flex items-center gap-2.5 font-heading text-xs tracking-brand text-faint uppercase">
                  <span
                    aria-hidden
                    className="grid size-7 place-items-center rounded-full border border-border-strong bg-foreground/10 text-[0.75rem] text-foreground"
                  >
                    {client.coach.charAt(0)}
                  </span>
                  Le mot de {client.coach}
                </p>
                <p className="mt-3 text-[1.0625rem] leading-relaxed text-pretty">
                  {client.semaine.mot}
                </p>
                <p className="mt-4 font-heading text-[0.75rem] tracking-brand text-muted-foreground uppercase transition-colors group-hover:text-foreground">
                  Lui répondre →
                </p>
              </Link>
            ) : null}
          </div>
        </section>

        {/* La fiche de la semaine — un bonus du coach, discret, tout en bas. */}
        {client.semaine.fiche ? (
          <section className="mt-12">
            <TitreZone titre="La fiche de la semaine" discret />
            <Carte>
              <p className="font-heading text-[1.0625rem] tracking-[0.02em] uppercase">
                {client.semaine.fiche.titre}
              </p>
              <p className="mt-2 text-[0.9375rem] text-muted-foreground">
                {client.semaine.fiche.resume}
              </p>
            </Carte>
          </section>
        ) : null}
      </div>
    </div>
  )
}

/* ------------------------------------------------------------- morceaux */

/**
 * L'en-tête d'une zone. La zone forte (« Aujourd'hui ») porte un grand titre et,
 * au plus, un compteur — jamais une phrase. Les zones de contexte (`discret`)
 * gardent un intitulé sobre.
 */
function TitreZone({
  titre,
  indication,
  discret,
}: {
  titre: string
  indication?: string
  discret?: boolean
}) {
  if (discret) {
    return (
      <h2 className="mb-4 font-heading text-xs tracking-brand text-faint uppercase">
        {titre}
      </h2>
    )
  }

  return (
    <div className="mb-5 flex items-baseline justify-between gap-4">
      <h2 className="font-heading text-[clamp(1.5rem,6vw,2rem)] leading-none font-medium uppercase">
        {titre}
      </h2>
      {indication ? (
        <span className="font-heading text-sm tracking-brand text-faint uppercase tabular-nums">
          {indication}
        </span>
      ) : null}
    </div>
  )
}

/** Un panneau borné. */
function Carte({
  className,
  children,
}: {
  className?: string
  children: React.ReactNode
}) {
  return (
    <div className={cn("rounded-lg border border-border bg-card p-5", className)}>
      {children}
    </div>
  )
}

/** Un bloc sans matière le dit franchement, plutôt qu'un cadre vide. Le
    sous-texte est optionnel — souvent le titre suffit. */
function EtatVide({
  titre,
  children,
}: {
  titre: string
  children?: React.ReactNode
}) {
  return (
    <div className="rounded-lg border border-border bg-card px-5 py-6">
      <p className="font-heading text-[1rem] tracking-[0.02em] uppercase">
        {titre}
      </p>
      {children ? (
        <p className="mt-2 text-[0.9375rem] text-faint">{children}</p>
      ) : null}
    </div>
  )
}

/**
 * La séance du jour, en héros — tant qu'elle est à faire.
 *
 * Une fois répondue, elle n'est plus ici : elle recule dans la liste du bas
 * (`LigneFaite`). Donc pas d'état « faite » ni de « Refaire » mis en avant —
 * seulement le geste utile : le nom, les étapes, un bouton « Commencer », et
 * deux petits liens pour qui l'a déjà faite autrement ou n'a pas pu.
 */
function CarteSeance({
  ligne,
  onStatut,
}: {
  ligne: Ligne
  onStatut: (tacheId: string, jour: number, statut: Statut) => void
}) {
  const nbEtapes = ligne.tache.deroule?.etapes.length ?? 0

  return (
    <div className="rounded-lg border border-border-strong bg-foreground/[0.05] px-5 py-5">
      <p className="font-heading text-xs tracking-brand text-faint uppercase">
        {ligne.enRetard ? `${JOURS[ligne.jour]} · à rattraper` : "Séance du jour"}
      </p>

      <p className="mt-2 font-heading text-[clamp(1.375rem,6vw,1.75rem)] leading-tight uppercase text-pretty">
        {ligne.tache.label}
      </p>
      {nbEtapes > 0 ? (
        <p className="mt-1.5 text-[0.9375rem] text-faint">{nbEtapes} étapes</p>
      ) : null}

      <Link
        href={`/mon-espace/seance/${ligne.tache.id}?jour=${ligne.jour}`}
        className="mt-5 flex items-center justify-center gap-2 rounded-lg border border-foreground bg-foreground px-4 py-4 font-heading text-[0.9375rem] tracking-brand text-background uppercase outline-none transition-colors hover:bg-foreground/90 focus-visible:ring-3 focus-visible:ring-ring/50"
      >
        <PlayIcon aria-hidden className="size-4" />
        Commencer
      </Link>

      <div className="mt-3 flex justify-center gap-5">
        <BoutonTexte onClick={() => onStatut(ligne.tache.id, ligne.jour, "fait")}>
          Déjà faite
        </BoutonTexte>
        <BoutonTexte onClick={() => onStatut(ligne.tache.id, ligne.jour, "manque")}>
          Pas faite
        </BoutonTexte>
      </div>
    </div>
  )
}

/**
 * Une carte du jour : une consigne simple, un geste évident.
 *
 * Tant qu'elle est à faire — deux boutons nommés, hauts sous le doigt. Dès
 * qu'on répond, la consigne quitte cette liste pour descendre dans le répondu
 * (`LigneFaite`). Donc pas d'état actif à peindre ici : au tap, elle s'en va.
 */
function CarteJour({
  ligne,
  onStatut,
}: {
  ligne: Ligne
  onStatut: (tacheId: string, jour: number, statut: Statut) => void
}) {
  return (
    <div
      className={cn(
        "rounded-lg border bg-card px-5 py-5",
        ligne.enRetard ? "border-border-strong" : "border-border"
      )}
    >
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="font-heading text-[1.1875rem] leading-tight tracking-[0.01em] uppercase">
          {ligne.tache.label}
        </p>
        {ligne.enRetard ? (
          <span className="shrink-0 rounded-sm border border-border-strong px-2 py-0.5 font-heading text-[0.6875rem] tracking-brand text-muted-foreground uppercase">
            {JOURS[ligne.jour]} · à rattraper
          </span>
        ) : null}
      </div>

      <div className="mt-4 flex gap-3">
        <BoutonStatut
          ton="fait"
          onClick={() => onStatut(ligne.tache.id, ligne.jour, "fait")}
        >
          <CheckIcon className="size-4" />
          C&apos;est fait
        </BoutonStatut>
        <BoutonStatut
          ton="manque"
          onClick={() => onStatut(ligne.tache.id, ligne.jour, "manque")}
        >
          <XIcon className="size-4" />
          Pas fait
        </BoutonStatut>
      </div>

      {ligne.tache.comment ? (
        <DetailComment
          comment={ligne.tache.comment}
          id={`${ligne.tache.id}-${ligne.jour}`}
        />
      ) : null}
    </div>
  )
}

/**
 * Une consigne déjà répondue — ça recule. Une petite ligne cochée (✓ fait,
 * ✕ pas fait), sobre et muette. Le tap la remet « à faire » : la seule marche
 * arrière, sans bouton qui s'étale.
 */
function LigneFaite({
  ligne,
  onStatut,
}: {
  ligne: Ligne
  onStatut: (tacheId: string, jour: number, statut: Statut) => void
}) {
  const statut = ligne.tache.statuts[ligne.jour] ?? "attente"

  return (
    <li>
      <button
        type="button"
        onClick={() => onStatut(ligne.tache.id, ligne.jour, "attente")}
        aria-label={`${ligne.tache.label} : ${LIBELLE_STATUT[statut]}. Remettre à faire.`}
        className="flex w-full items-center gap-3 rounded-lg px-2 py-2.5 text-left outline-none transition-colors hover:bg-foreground/[0.03] focus-visible:ring-3 focus-visible:ring-ring/50"
      >
        <Pastille statut={statut} />
        <span className="flex-1 truncate text-[0.9375rem] text-muted-foreground">
          {ligne.tache.label}
        </span>
        {ligne.enRetard ? (
          <span className="font-heading text-[0.6875rem] tracking-brand text-faint uppercase">
            {JOURS[ligne.jour]}
          </span>
        ) : null}
      </button>
    </li>
  )
}

/**
 * Le « comment faire » d'une consigne, replié.
 *
 * Un bouton nommé (pas un geste à deviner) ouvre la voix du coach. Fermé par
 * défaut — l'action du jour reste au premier plan, le « comment » vient à la
 * demande.
 */
function DetailComment({
  comment,
  id,
}: {
  comment: CommentFaire
  id: string
}) {
  const [ouvert, setOuvert] = React.useState(false)
  const regionId = `comment-${id}`

  return (
    <div className="mt-4 border-t border-border pt-3">
      <button
        type="button"
        onClick={() => setOuvert((o) => !o)}
        aria-expanded={ouvert}
        aria-controls={regionId}
        className="flex w-full items-center justify-between gap-3 rounded-sm text-left font-heading text-[0.75rem] tracking-brand text-muted-foreground uppercase outline-none transition-colors hover:text-foreground focus-visible:ring-3 focus-visible:ring-ring/50"
      >
        Comment faire
        <ChevronDownIcon
          aria-hidden
          className={cn(
            "size-4 shrink-0 transition-transform duration-200",
            ouvert && "rotate-180"
          )}
        />
      </button>

      <div id={regionId} hidden={!ouvert} className="mt-3">
        {comment.intro ? (
          <p className="text-[0.9375rem] leading-relaxed text-pretty text-muted-foreground">
            {comment.intro}
          </p>
        ) : null}
        {comment.points.length > 0 ? (
          <ul className={cn("grid gap-2", comment.intro && "mt-3")}>
            {comment.points.map((point, index) => (
              <li
                key={index}
                className="flex gap-2.5 text-[0.9375rem] leading-relaxed text-muted-foreground"
              >
                <span
                  aria-hidden
                  className="mt-[0.6875rem] h-px w-2.5 shrink-0 bg-border-strong"
                />
                <span className="text-pretty">{point}</span>
              </li>
            ))}
          </ul>
        ) : null}
      </div>
    </div>
  )
}

/**
 * Répondre à une consigne : « C'est fait » plein, « Pas fait » cerné. Hauts et
 * larges, pour un doigt qui ne vise pas au pixel. Au tap, la consigne rejoint
 * le répondu — pas d'état actif à peindre ici.
 */
function BoutonStatut({
  ton,
  onClick,
  children,
}: {
  ton: "fait" | "manque"
  onClick: () => void
  children: React.ReactNode
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "flex flex-1 items-center justify-center gap-2 rounded-lg border px-4 py-3.5 font-heading text-[0.875rem] tracking-brand uppercase transition-colors",
        ton === "fait"
          ? "border-foreground bg-foreground text-background hover:bg-foreground/90"
          : "border-border text-muted-foreground hover:border-border-strong hover:text-foreground"
      )}
    >
      {children}
    </button>
  )
}

/** Un lien-texte discret, pour les gestes secondaires (marquer sans traverser). */
function BoutonTexte({
  onClick,
  children,
}: {
  onClick: () => void
  children: React.ReactNode
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="rounded-sm font-heading text-[0.75rem] tracking-brand text-faint uppercase outline-none transition-colors hover:text-foreground focus-visible:ring-3 focus-visible:ring-ring/50"
    >
      {children}
    </button>
  )
}

/** La pastille du répondu (`LigneFaite`) : ✓ fait, ✕ pas fait, cerné sinon. */
function Pastille({ statut, terne }: { statut: Statut; terne?: boolean }) {
  return (
    <span
      className={cn(
        "grid size-6 place-items-center rounded-full border",
        statut === "fait" && "border-foreground bg-foreground",
        statut === "manque" && "border-border-strong",
        statut === "attente" && (terne ? "border-border" : "border-border-strong")
      )}
    >
      {statut === "fait" ? (
        <CheckIcon className="size-3.5 text-background" />
      ) : null}
      {statut === "manque" ? <XIcon className="size-3 text-faint" /> : null}
    </span>
  )
}

/** L'état d'un jour de la semaine, agrégé sur ses consignes. */
type EtatJour = "tenu" | "aujourdhui" | "aujourdhuiTenu" | "manque" | "prevu" | "repos"

const LIBELLE_JOUR: Record<EtatJour, string> = {
  tenu: "tenu",
  aujourdhui: "aujourd'hui",
  aujourdhuiTenu: "aujourd'hui, tenu",
  manque: "pas tenu",
  prevu: "à venir",
  repos: "repos",
}

/** L'état d'un jour, agrégé sur ses consignes — le cœur lisible de l'agenda. */
function etatJour(taches: Tache[], jour: number, aujourdhui: number): EtatJour {
  const duJour = taches.filter((t) => t.jours.includes(jour))
  if (duJour.length === 0) return "repos"
  const tenu = duJour.every((t) => (t.statuts[jour] ?? "attente") === "fait")
  if (jour === aujourdhui) return tenu ? "aujourdhuiTenu" : "aujourdhui"
  if (tenu) return "tenu"
  if (jour < aujourdhui) return "manque"
  return "prevu"
}

/**
 * Le jour de la semaine où tombe la prochaine séance, lu dans son libellé.
 *
 * Le libellé du coach est du texte libre (« Jeudi 13 août, 18 h 30 ») : on y
 * cherche simplement le nom d'un jour. Rien trouvé (ou séance hors semaine) →
 * pas d'épingle, plutôt qu'une épingle au mauvais jour.
 */
function jourDuRdv(quand: string): number | null {
  const bas = quand.toLowerCase()
  for (let i = 0; i < JOURS.length; i++) if (bas.includes(JOURS[i])) return i
  return null
}

/**
 * La semaine en cours, en agenda — pas un simple calendrier de pastilles.
 *
 * Une ligne par jour : le quantième, l'état d'un coup d'œil (le point rempli
 * quand c'est tenu), *ce qui est prévu* ce jour-là, et le RDV du coach épinglé
 * là où il tombe. Aujourd'hui ressort. En pied, où j'en suis de ma semaine et
 * la série — le ressort qui fait revenir.
 */
function AgendaSemaine({
  taches,
  aujourdhui,
  datesSemaine,
  rdvJour,
  rdvLabel,
  tenue,
  bilan,
  serie,
}: {
  taches: Tache[]
  aujourdhui: number
  datesSemaine: number[]
  rdvJour: number | null
  rdvLabel: string
  tenue: { titre: string; detail?: string }
  bilan: BilanSemaine
  serie: number
}) {
  return (
    <div className="flex h-full flex-col rounded-lg border border-border-strong bg-foreground/[0.05] p-5">
      <p className="font-heading text-xs tracking-brand text-foreground uppercase">
        Cette semaine
      </p>

      <ul className="mt-4 grid gap-0.5">
        {JOURS.map((_, jour) => (
          <LigneAgenda
            key={jour}
            jour={jour}
            etat={etatJour(taches, jour, aujourdhui)}
            date={datesSemaine[jour]}
            consignes={taches
              .filter((t) => t.jours.includes(jour))
              .map((t) => t.label)}
            rdv={jour === rdvJour ? rdvLabel : undefined}
            estAujourdhui={jour === aujourdhui}
          />
        ))}
      </ul>

      <div className="mt-auto flex items-center justify-between gap-3 border-t border-border pt-4">
        <p className="font-heading text-[0.9375rem] leading-none uppercase">
          {tenue.titre}
          {tenue.detail ? (
            <span className="ml-2 text-[0.75rem] tracking-brand text-faint">
              · {tenue.detail}
            </span>
          ) : null}
        </p>
        {serie >= 2 ? (
          <span className="inline-flex shrink-0 items-center gap-1.5">
            <FlameIcon aria-hidden className="size-4 text-foreground" />
            <span className="font-heading text-[0.8125rem] leading-none uppercase">
              {serie} sem.
            </span>
          </span>
        ) : (
          <span className="font-heading text-[0.8125rem] tracking-brand text-faint tabular-nums">
            {bilan.fait} / {bilan.total}
          </span>
        )}
      </div>
    </div>
  )
}

/** Une ligne de l'agenda : un jour, son état, ce qui y est prévu, le RDV éventuel. */
function LigneAgenda({
  jour,
  etat,
  date,
  consignes,
  rdv,
  estAujourdhui,
}: {
  jour: number
  etat: EtatJour
  date: number
  consignes: string[]
  rdv?: string
  estAujourdhui: boolean
}) {
  const repos = consignes.length === 0 && !rdv

  return (
    <li
      className={cn(
        "flex items-center gap-3 rounded-md px-2 py-2",
        estAujourdhui && "bg-foreground/[0.07]"
      )}
    >
      <div className="flex w-8 shrink-0 flex-col items-center leading-none">
        <span
          className={cn(
            "font-heading text-[0.625rem] tracking-brand uppercase",
            estAujourdhui ? "text-foreground" : "text-faint"
          )}
        >
          {JOURS_COURTS[jour]}
        </span>
        <span
          className={cn(
            "mt-1 font-heading text-[0.9375rem] tabular-nums",
            estAujourdhui ? "text-foreground" : "text-muted-foreground"
          )}
        >
          {date}
        </span>
      </div>

      <JourDot etat={etat} label={`${JOURS[jour]} : ${LIBELLE_JOUR[etat]}`} />

      <div className="min-w-0 flex-1">
        {repos ? (
          <span className="text-[0.8125rem] text-faint">Repos</span>
        ) : (
          <>
            {consignes.length > 0 ? (
              <span className="block truncate text-[0.875rem] text-muted-foreground">
                {consignes.join(" · ")}
              </span>
            ) : null}
            {rdv ? (
              <span className="mt-1 inline-flex items-center rounded-sm border border-border-strong bg-foreground/10 px-1.5 py-0.5 font-heading text-[0.625rem] tracking-brand text-foreground uppercase">
                {rdv}
              </span>
            ) : null}
          </>
        )}
      </div>
    </li>
  )
}

/**
 * Une semaine à venir dans l'agenda — datée, pas encore remplie.
 *
 * Le coach publie le programme chaque dimanche : on ne peint donc pas de fausses
 * consignes. Un cadre en pointillés, la trame des sept jours en creux et un mot
 * honnête (« à venir ») disent l'attente sans mentir sur ce qui existe.
 */
function AgendaAVenir({ libelle, coach }: { libelle: string; coach: string }) {
  return (
    <div className="flex h-full flex-col rounded-lg border border-dashed border-border bg-card/40 p-5">
      <p className="font-heading text-xs tracking-brand text-faint uppercase">
        {libelle}
      </p>

      <div className="mt-5 grid grid-cols-7 gap-1">
        {JOURS_COURTS.map((court, jour) => (
          <div key={jour} className="flex flex-col items-center gap-2.5">
            <span className="font-heading text-[0.625rem] tracking-brand text-faint uppercase">
              {court}
            </span>
            <span aria-hidden className="size-7 rounded-full border-2 border-border" />
          </div>
        ))}
      </div>

      <div className="mt-auto pt-6">
        <p className="font-heading text-[0.9375rem] leading-none text-muted-foreground uppercase">
          À venir
        </p>
        <p className="mt-2 text-[0.8125rem] leading-relaxed text-faint text-pretty">
          {coach} publiera votre programme d&apos;ici dimanche.
        </p>
      </div>
    </div>
  )
}

/** Le point d'un jour. Rempli = tenu, entouré = aujourd'hui, en creux = à venir. */
function JourDot({ etat, label }: { etat: EtatJour; label: string }) {
  const plein = etat === "tenu" || etat === "aujourdhuiTenu"

  return (
    <span role="img" aria-label={label} className="grid size-8 place-items-center">
      {etat === "repos" ? (
        <span aria-hidden className="size-1.5 rounded-full bg-border" />
      ) : (
        <span
          aria-hidden
          className={cn(
            "grid size-8 place-items-center rounded-full border-2 transition-colors",
            plein && "border-foreground bg-foreground",
            etat === "aujourdhui" && "border-foreground",
            etat === "manque" && "border-border-strong",
            etat === "prevu" && "border-border"
          )}
        >
          {plein ? <CheckIcon className="size-4 text-background" /> : null}
        </span>
      )}
    </span>
  )
}

export { Dashboard }
